import { FC, useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { PropertyFormData, Property, PropertyStatus } from '@/types/index';
import { Button } from '@components/common/Button';
import { StepIndicator } from '@components/common/StepIndicator';
import { Step1General } from './steps/Step1General';
import { Step2Annexes } from './steps/Step2Annexes';
import { Step3Surfaces } from './steps/Step3Surfaces';
import { Step4State } from './steps/Step4State';
import { Step5Finances } from './steps/Step5Finances';
import { Step6Photos } from './steps/Step6Photos';
import { apiClient } from '@services/api-client';
import { authService } from '@services/auth-service';
import { track } from '@services/analytics';
import { useNotificationStore } from '@/store/useNotificationStore';

interface PropertyFormWizardProps {
  property?: Property;
  onSuccess: () => void;
  onCancel: () => void;
  onSave?: () => void;
}

const STEPS = [
  { number: 1, title: 'Général' },
  { number: 2, title: 'Annexes' },
  { number: 3, title: 'Surfaces' },
  { number: 4, title: 'État' },
  { number: 5, title: 'Finances' },
  { number: 6, title: 'Photos' },
];

export const PropertyFormWizard: FC<PropertyFormWizardProps> = ({
  property,
  onSuccess,
  onCancel,
  onSave,
}) => {
  const [currentStep, setCurrentStep] = useState(1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [userSettings, setUserSettings] = useState<{ equityRate: number; acquisitionFees: number } | null>(null);
  const [savedPropertyId, setSavedPropertyId] = useState<string | undefined>(property?.id);
  const [isSaving, setIsSaving] = useState(false);
  const { addNotification } = useNotificationStore();

  // Charger les settings utilisateur au mount
  useEffect(() => {
    const loadSettings = async () => {
      try {
        const settings = await authService.getSettings();
        setUserSettings({
          equityRate: settings.equityRate,
          acquisitionFees: settings.acquisitionFees,
        });
      } catch (error) {
        console.error('Error loading user settings:', error);
      }
    };
    loadSettings();
  }, []);

  const {
    register,
    handleSubmit,
    formState: { errors },
    watch,
    setValue,
    trigger,
  } = useForm<PropertyFormData>({
    mode: 'onSubmit',
    defaultValues: property
      ? {
          address: property.address || '',
          postalCode: property.postalCode || '',
          city: property.city || '',
          strategy: property.strategy || '',
          affectation: property.affectation || '',
          status: property.status || PropertyStatus.FIRST_SELECTION,
          floorsInBuilding: property.floorsInBuilding || 1,
          floor: property.floor || 'ground',
          rooms: property.rooms || 1,
          bathrooms: property.bathrooms || 0,
          hasGuestToilet: property.hasGuestToilet || false,
          hasElevator: property.hasElevator || false,
          hasCellar: property.hasCellar || false,
          hasAttic: property.hasAttic || false,
          coveredParkingCount: property.coveredParkingCount || 0,
          uncoveredParkingCount: property.uncoveredParkingCount || 0,
          hasIndividualGarage: property.hasIndividualGarage || false,
          area: property.area || 1,
          exteriorArea: property.exteriorArea || undefined,
          exteriorType: property.exteriorType || 'balcony',
          buildingYear: property.buildingYear || undefined,
          buildingCondition: property.buildingCondition || '',
          apartmentCondition: property.apartmentCondition || '',
          buildingGeneralAspect: property.buildingGeneralAspect || 'average',
          buildingStructure: property.buildingStructure || 'average',
          buildingRoof: property.buildingRoof || 'average',
          buildingFacades: property.buildingFacades || 'average',
          buildingWindows: property.buildingWindows || 'average',
          buildingCorridors: property.buildingCorridors || 'average',
          buildingTechnicalInstall: property.buildingTechnicalInstall || 'average',
          hasCEBC: property.hasCEBC || false,
          hasMinergie: property.hasMinergie || false,
          condPlastering: property.condPlastering || 'average',
          condParquet: property.condParquet || 'average',
          condTiling: property.condTiling || 'average',
          condElectricity: property.condElectricity || 'average',
          condPlumbing: property.condPlumbing || 'average',
          condJoinery: property.condJoinery || 'average',
          condKitchen: property.condKitchen || 'average',
          askingPrice: property.askingPrice || undefined,
          ppeCharges: property.ppeCharges || undefined,
          isRented: property.isRented || false,
          rentPrice: property.rentPrice || undefined,
          rentIncludesCharges: property.rentIncludesCharges || false,
          downPayment: property.downPayment || undefined,
          egid: property.egid || undefined,
        }
      : {
          address: '',
          postalCode: '',
          city: '',
          strategy: '',
          affectation: '',
          status: PropertyStatus.FIRST_SELECTION,
          floorsInBuilding: 1,
          floor: 'ground',
          rooms: 1,
          bathrooms: 0,
          hasGuestToilet: false,
          hasElevator: false,
          hasCellar: false,
          hasAttic: false,
          coveredParkingCount: 0,
          uncoveredParkingCount: 0,
          hasIndividualGarage: false,
          area: 1,
          exteriorType: 'balcony',
          isRented: false,
          rentIncludesCharges: false,
          buildingGeneralAspect: 'average',
          buildingStructure: 'average',
          buildingRoof: 'average',
          buildingFacades: 'average',
          buildingWindows: 'average',
          buildingCorridors: 'average',
          buildingTechnicalInstall: 'average',
          hasCEBC: false,
          hasMinergie: false,
          condPlastering: 'average',
          condParquet: 'average',
          condTiling: 'average',
          condElectricity: 'average',
          condPlumbing: 'average',
          condJoinery: 'average',
          condKitchen: 'average',
        },
  });

  // Validation des champs par étape
  const validateStep = async (step: number): Promise<boolean> => {
    let fieldsToValidate: (keyof PropertyFormData)[] = [];

    switch (step) {
      case 1:
        fieldsToValidate = [
          'address',
          'postalCode',
          'city',
          'strategy',
          'affectation',
          'status',
          'floorsInBuilding',
          'floor',
          'rooms',
          'bathrooms',
        ];
        break;
      case 2:
        fieldsToValidate = ['coveredParkingCount', 'uncoveredParkingCount'];
        break;
      case 3:
        fieldsToValidate = ['area'];
        break;
      case 4:
        fieldsToValidate = ['buildingYear'];
        break;
      case 5:
        fieldsToValidate = ['askingPrice', 'ppeCharges', 'rentPrice', 'downPayment'];
        break;
    }

    const result = await trigger(fieldsToValidate);
    return result;
  };

  const handleNext = async () => {
    const isValid = await validateStep(currentStep);
    if (!isValid) return;

    // Si étape 5 et pas encore sauvegardé, créer la propriété MAINTENANT
    if (currentStep === 5 && !savedPropertyId) {
      const formData = watch();
      try {
        setIsSubmitting(true);
        const propertyData = {
          ...formData,
          country: 'CH',
        };

        const response = await apiClient.post<{ id: string }>('/properties', propertyData);
        setSavedPropertyId(response.id);
        setCurrentStep(6);  // Aller à l'étape photos
      } catch (error) {
        console.error('Erreur sauvegarde:', error);
        alert('Erreur lors de l\'enregistrement de la propriété');
      } finally {
        setIsSubmitting(false);
      }
    } else {
      setCurrentStep((prev) => Math.min(prev + 1, STEPS.length));
    }
  };

  const handleBack = () => {
    setCurrentStep((prev) => Math.max(prev - 1, 1));
  };

  const handleSave = async () => {
    try {
      setIsSaving(true);
      const formData = watch();
      const propertyData = { ...formData, country: 'CH' };

      if (property?.id || savedPropertyId) {
        const idToUpdate = property?.id || savedPropertyId;
        await apiClient.put(`/properties/${idToUpdate}`, propertyData);
      } else {
        const response = await apiClient.post<{ id: string }>('/properties', propertyData);
        setSavedPropertyId(response.id);
      }

      onSave?.();
      addNotification({
        type: 'success',
        title: 'Enregistré',
        message: 'Les modifications ont été sauvegardées.',
        duration: 3000,
      });
    } catch (error) {
      console.error('Erreur sauvegarde:', error);
      addNotification({
        type: 'error',
        title: 'Erreur',
        message: "Impossible d'enregistrer les modifications.",
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleStepClick = async (targetStep: number) => {
    // Guard: Don't do anything if clicking current step
    if (targetStep === currentStep) {
      return;
    }

    // Backward navigation: No validation required
    if (targetStep < currentStep) {
      setCurrentStep(targetStep);
      return;
    }

    // Forward navigation: Validate all intermediate steps
    for (let step = currentStep; step < targetStep; step++) {
      const isValid = await validateStep(step);

      if (!isValid) {
        // Stop at first invalid step
        setCurrentStep(step);
        return;
      }
    }

    // All intermediate steps valid: Navigate to target
    setCurrentStep(targetStep);
  };

  const handleFinalSubmit = async (data: PropertyFormData) => {
    try {
      setIsSubmitting(true);

      // Si on est sur Step 6 et que la propriété a déjà été sauvegardée, juste fermer
      if (currentStep === 6 && savedPropertyId && !property?.id) {
        onSuccess();
        return;
      }

      // Préparer les données pour l'API
      const propertyData = {
        ...data,
        country: 'CH',
      };

      if (property?.id || savedPropertyId) {
        // Mise à jour
        const idToUpdate = property?.id || savedPropertyId;
        await apiClient.put(`/properties/${idToUpdate}`, propertyData);
      } else {
        // Création (ne devrait pas arriver si on passe par Step 6)
        const response = await apiClient.post<{ id: string }>('/properties', propertyData);
        setSavedPropertyId(response.id);
      }

      track(property?.id ? 'property-updated' : 'property-created');
      onSuccess();
    } catch (error) {
      console.error('Erreur lors de la soumission:', error);
      alert('Une erreur est survenue lors de l\'enregistrement de la propriété');
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderStep = () => {
    const commonProps = {
      register,
      errors,
      setValue,
      watch,
    };

    switch (currentStep) {
      case 1:
        return <Step1General {...commonProps} />;
      case 2:
        return <Step2Annexes {...commonProps} />;
      case 3:
        return <Step3Surfaces {...commonProps} />;
      case 4:
        return <Step4State {...commonProps} />;
      case 5:
        return <Step5Finances setValue={setValue} watch={watch} userSettings={userSettings || undefined} />;
      case 6:
        return <Step6Photos propertyId={savedPropertyId} />;
      default:
        return null;
    }
  };

  const handleFormSubmit = () => {
    if (currentStep === STEPS.length) {
      handleSubmit(handleFinalSubmit)();
    } else {
      handleNext();
    }
  };

  return (
    <div className="w-full max-w-4xl mx-auto p-6">
      {/* Step Indicator */}
      <StepIndicator
        currentStep={currentStep}
        totalSteps={STEPS.length}
        steps={STEPS}
        onStepClick={handleStepClick}
      />

      {/* Form */}
      <form onSubmit={(e) => {
        e.preventDefault();
        handleFormSubmit();
      }} className="mt-8">
        {/* Step Content */}
        <div className="mb-8">{renderStep()}</div>

        {/* Navigation Buttons */}
        <div className="flex justify-between items-center pt-6 border-t border-gray-200 dark:border-gray-700">
          <div>
            {currentStep > 1 && (
              <Button
                type="button"
                variant="secondary"
                onClick={handleBack}
                disabled={isSubmitting || isSaving}
              >
                Retour
              </Button>
            )}
          </div>

          <div className="flex gap-3">
            <Button
              type="button"
              variant="ghost"
              onClick={onCancel}
              disabled={isSubmitting || isSaving}
            >
              Annuler
            </Button>

            <Button
              type="submit"
              variant="primary"
              disabled={isSubmitting || isSaving}
            >
              {isSubmitting
                ? 'Enregistrement...'
                : currentStep === 5 && !savedPropertyId
                ? 'Enregistrer et continuer'
                : currentStep === STEPS.length
                ? 'Terminer'
                : 'Suivant'}
            </Button>

            <Button
              type="button"
              variant="secondary"
              onClick={handleSave}
              disabled={isSubmitting || isSaving}
            >
              {isSaving ? 'Sauvegarde...' : 'Enregistrer'}
            </Button>
          </div>
        </div>
      </form>
    </div>
  );
};
